import * as md5 from 'md5';
import * as browserStorage from 'data/browserStorage';
import { COOKIE_STORAGE } from '../../data/browserStorage/constants';
import { UserRoles as bronteRoles } from '../../constants/enums';
import workspaceService from 'services/WorkspaceService';
import GTMResolver from './GTMResolver';
import { gtmTrackPageView, gtmTrackGlobalLaunchEventHandler, gtmTrackGlobalObligatoryLaunchEventHandler, gtmTrackUserExceptionEventHandler, gtmTrackUserInteractionActionEventHandler, convertKeysToCamelCase, trackUserInteractionActionEventHandler } from './gtmUtils';
import {
  baseGtmAnalyticsConstants,
  defaultGTMDataLayerPayload,
  GTM_EVENT_TYPES,
  GTM_CDP_EVENT_NAMES,
  GTM_CDP_TRACK_PAGEVIEW_EVENT,
  GTM_CDP_TRACK_USER_INTERACTION_EVENT,
  GTM_CDP_TRACK_USER_EXCEPTION_EVENT,
  TRACK_USER_INTERACTION,
} from './constants';
import { subscribeToEvent } from 'services/EventService';
import { clearCoursewareContext, getCoursewareContext } from 'utils/coursewareContext';

export const SESSION_ID = 'sessionId';
let gtmPluginEventBusSubscribed;

const cdpEventToGtmEventTypeMap = {
  [GTM_CDP_TRACK_PAGEVIEW_EVENT]: GTM_EVENT_TYPES.PAGEVIEW,
  [GTM_CDP_TRACK_USER_INTERACTION_EVENT]: GTM_EVENT_TYPES.USER_INTERACTION,
  [GTM_CDP_TRACK_USER_EXCEPTION_EVENT]: GTM_EVENT_TYPES.USER_EXCEPTION,
  [TRACK_USER_INTERACTION]: GTM_EVENT_TYPES.TRACK_USER_INTERACTION,
};

/**
 * Subscribes to plugin/CDP-emitted event names on the workspace EventService bus
 * (same bus CDP's EVENT_SERVICE delegates to). Avoids importing CDP/plugin-api
 * from workspace shell code, which would create a circular dependency.
 */
export function gtmCdpEventSubscriptionHandler(gtmTrackEventHandler) {
  if (typeof gtmTrackEventHandler !== 'function') {
    // eslint-disable-next-line no-console
    console.warn('[GTM][EventService] subscribe skipped (no gtmTrackEvent handler)');
    return;
  }

  if (gtmPluginEventBusSubscribed) {
    return;
  }

  for (const eventName of GTM_CDP_EVENT_NAMES) {
    subscribeToEvent(eventName, (payload) => {
      // eslint-disable-next-line no-console
      console.log('[GTM][EventService]', eventName, payload ?? '(no-payload)');
      const gtmEventType = cdpEventToGtmEventTypeMap[eventName];
      if (!gtmEventType) {
        // eslint-disable-next-line no-console
        console.warn(
          '[GTM][EventService] no GTM mapping for event',
          eventName,
          payload ?? '(no-payload)',
        );
        return;
      }

      gtmTrackEventHandler({ eventType: gtmEventType, payload: convertKeysToCamelCase(payload) ?? {} });
    });
  }

  gtmPluginEventBusSubscribed = true;
  // eslint-disable-next-line no-console
  console.log('[GTM][EventService] subscribed', GTM_CDP_EVENT_NAMES.length, GTM_CDP_EVENT_NAMES);
}

export default (() => {
  let gtmResolver;
  let isRegistered = false;
 
  // Caching the userParams here
  let _userParams;

  const getWorkspaceIdForAnalytics = () => {
    const { id } = workspaceService.getSelectedWorkspace() || {};
    return id || null;
  };

  const getBaseAnalyticsPayload = () => ({
    ...baseGtmAnalyticsConstants,
    person_role_code: _userParams?.personRoleCode ?? null,
    organization_id: _userParams?.organizationId ?? null,
    workspace_id: getWorkspaceIdForAnalytics(),
  });

  const getCoursewareAnalyticsPayload = () => {
    const { courseId, lessonId, chapterId } = getCoursewareContext();
    return {
      courseId: courseId || null,
      lessonId: lessonId || null,
      chapterId: chapterId || null,
    };
  };

  /**
   * Method to set user params
   * @param currentUser - Object containing account info
   */
  const setUserParams = (currentUser) => {
    const { bearerToken } = gtmResolver.getBearerInfo();
    const sessionId = getSessionIdFromBearerToken(bearerToken);
    _userParams = {
      personId: currentUser.accountId || null,
      personIdType: currentUser.authenticationType || defaultGTMDataLayerPayload.person_id_type,
      personRoleCode: getPearsonRoleCode(currentUser.roles),
      sessionId: sessionId || null,
      organizationId: currentUser.subscriptionId || null,
    };

    const sessionIdFromCookie = browserStorage.load(SESSION_ID, COOKIE_STORAGE);
    if (!sessionIdFromCookie || sessionIdFromCookie !== sessionId) {
      // only run once when login is successful

      browserStorage.save(SESSION_ID, sessionId, COOKIE_STORAGE);    
      gtmTrackEvent({ eventType: GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH, payload: { event_action: 'login',..._userParams} });
    }
  };

 

  /**
   * Bronte roles to GTM roles
   * @param roles - One of INSTRUCTOR, AERO_INSTRUCTOR, STUDENT, STUDENT_GUEST, DEVELOPER, ADMIN, SUPPORT
   * @returns {string} - One of ["Student", "Instructor", "TeachingAssistant", "Parent", "Administrator","Employee", "Other"]
   */
  const getPearsonRoleCode = (roles = []) => {
    if (roles.includes(bronteRoles.SUPPORT)) {
      return 'Employee'; // Make consts in file
    }
    if (roles.includes(bronteRoles.ADMIN)) {
      return 'Administrator';
    }
    if (roles.includes(bronteRoles.DEVELOPER)) {
      return 'TeachingAssistant';
    }
    if (roles.includes(bronteRoles.AERO_INSTRUCTOR)) {
      return 'Instructor';
    }
    if (roles.includes(bronteRoles.STUDENT || roles.includes(bronteRoles.STUDENT_GUEST))) {
      return 'STUDENT';
    }
    return 'OTHER';
  };

  const getSessionIdFromBearerToken = (bearerToken) => md5(bearerToken);

  /**
   * Method to push users logout event
   */
  const pushUserLogoutEvent = () => {
    // Clear cached values on logout
    _userParams = undefined;
    clearCoursewareContext();
    gtmTrackEvent({ eventType: GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH, payload: { event_action: 'logout'} });

  };

  function gtmTrackEvent({ eventType, payload }) {
    switch (eventType) {
      case GTM_EVENT_TYPES.GLOBAL_LAUNCH:
        gtmTrackGlobalLaunchEventHandler(payload);
        return;

      case GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH:
        gtmTrackGlobalObligatoryLaunchEventHandler(payload);
        return;

      case GTM_EVENT_TYPES.PAGEVIEW:
        gtmTrackPageView({
          ...getCoursewareAnalyticsPayload(),
          ...payload,
        });
        return;

      case GTM_EVENT_TYPES.USER_INTERACTION:
        gtmTrackUserInteractionActionEventHandler({
          ...getBaseAnalyticsPayload(),
          ...getCoursewareAnalyticsPayload(),
          ...payload,
        });
        return;

      case GTM_EVENT_TYPES.TRACK_USER_INTERACTION:
        trackUserInteractionActionEventHandler({
          ...getBaseAnalyticsPayload(),
          ...getCoursewareAnalyticsPayload(),
          ...payload,
        });
        return;

      case GTM_EVENT_TYPES.USER_EXCEPTION:
        gtmTrackUserExceptionEventHandler(payload);
        return;
      default:
        // eslint-disable-next-line no-console
        console.warn("Unknown GTM event type:", eventType);
        return;
    }
  }

  return {
    isRegistered: () => isRegistered,
    register: (connection) => {
      isRegistered = true;
      gtmResolver = new GTMResolver(connection);
    },
    setUserParams, // (login)
    pushUserLogoutEvent, // (logout)
    gtmTrackEvent, // (pageview, user_interaction, user_exception)
    gtmCdpEventSubscriptionHandler,
  };
})();
